import { isArray, isObject, omit, isNil } from "lodash"
import { ASSET_TYPES } from "helpers/assets/constants"

export const GROUP_BY_TYPES = {
  CLASSIFICATION: "CLASSIFICATION",
  TAG: "TAG",
  POSITION_ATTRIBUTE: "POSITION_ATTRIBUTE",
}

export function getGroupByOptions(groupByOptions) {
  // Handle null/undefined case
  if (!groupByOptions) {
    return { groupBy: null, groupById: null, tier: null }
  }

  if (!isObject(groupByOptions)) {
    return { groupBy: groupByOptions, groupById: null, tier: null }
  }

  // Handle classification group
  if (groupByOptions.isClassification) {
    const classificationId = groupByOptions.classificationId || groupByOptions.id
    return {
      groupBy: GROUP_BY_TYPES.CLASSIFICATION,
      groupById: classificationId,
      tier: groupByOptions.id,
    }
  }

  // Handle tag group
  if (groupByOptions.isTag) {
    return {
      groupBy: GROUP_BY_TYPES.TAG,
      groupById: groupByOptions.id,
      tier: null,
    }
  }

  // Handle position attribute group
  if (groupByOptions.isPositionAttribute) {
    return {
      groupBy: GROUP_BY_TYPES.POSITION_ATTRIBUTE,
      groupById: groupByOptions.id,
      tier: null,
    }
  }

  // Handle custom group by type
  if (groupByOptions.id) {
    return {
      groupBy: groupByOptions.id,
      groupById: null,
      tier: null,
    }
  }
}

export function getGroupByFields(groupByOptions) {
  const { groupBy, groupById, tier } = getGroupByOptions(groupByOptions)
  return {
    groupBy,
    ...(groupById && { groupById }),
    ...(tier && { tier }),
  }
}

export function getGroupByQueryParameters({ groupBy, groupById, tier } = {}) {
  const parameters = []

  if (groupBy) {
    parameters.push("$groupBy: ContributorGroupType!")
  }
  if (groupById) {
    parameters.push("$groupById: String!")
  }
  if (tier) {
    parameters.push("$tier: String!")
  }

  return parameters.length ? `, ${parameters.join(", ")}` : ""
}

export function getGroupBySubqueryParameters({ groupBy, groupById, tier } = {}) {
  const parameters = []

  if (groupBy) {
    parameters.push("groupBy: $groupBy")
  }
  if (groupById) {
    parameters.push("groupById: $groupById")
  }
  if (tier) {
    parameters.push("classificationTier: $tier")
  }

  return parameters.join(", ")
}

export function sanitizePositionSet(positionSet) {
  if (!positionSet?.id && !isArray(positionSet?.dates)) {
    return {}
  }

  if (positionSet.id) {
    return positionSet
  }

  // Inline position sets cannot have attributes
  const assetTypeEntries = ASSET_TYPES.map(assetType => [assetType, null])
  const emptyPositions = Object.fromEntries(assetTypeEntries)

  const sanitizedDates = positionSet.dates.map(date => ({
    ...date,
    ...ASSET_TYPES.reduce((acc, assetType) => ({
      ...acc,
      [assetType]: date[assetType]?.map(position => omit(position, "attributes")) || [],
    }), emptyPositions),
  }))

  return {
    ...positionSet,
    dates: sanitizedDates,
  }
}

export function getBetaInstrumentFromOptions(options) {
  if (!options.betaInstrument || options.isIntraday) {
    return null
  }

  return {
    id: options.betaInstrument.id,
    type: options.betaInstrument.type,
  }
}

export function getIsNoLongerHeldFromContributor(contributor) {
  if (contributor.notHeld) {
    return false
  }
  if (!isNil(contributor.positionSetEconomicExposure)) {
    return false
  }
  return isNil(contributor.values?.composition?.economicExposure)
}
